import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { 
  Flame, 
  Trophy, 
  Calendar, 
  Target,
  Zap,
  Star,
  Award,
  TrendingUp,
  CheckCircle,
  Info,
  Share2,
  Sun,
  X,
  Crown,
  Sparkles,
  Rocket
} from 'lucide-react';
import { useUserData } from '../contexts/UserDataContext';
import { getStreakStats, logDailyActivity } from '../utils/studyStreakManager';

const StudyStreakCard = ({ userId }) => {
  const { userData } = useUserData();
  const [streakData, setStreakData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showInfo, setShowInfo] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    const loadStreakData = async () => {
      if (!userId) return;
      
      try {
        setLoading(true);
        const stats = await getStreakStats(userId);
        setStreakData(stats);
        setError(null);
      } catch (err) {
        // Error loading streak data
        setError('Failed to load streak data');
        // Fallback to basic calculation from activities
        if (userData?.activities) {
          const basicStreak = calculateBasicStreak(userData.activities);
          setStreakData({
            currentStreak: basicStreak,
            longestStreak: basicStreak,
            totalActiveDays: basicStreak,
            isActiveToday: true,
            wasActiveYesterday: false,
            lastActivityDate: new Date().toDateString(),
            nextMilestone: 7,
            userName: null
          });
        }
      } finally {
        setLoading(false);
      }
    };

    loadStreakData();
  }, [userId, userData]);

  const calculateBasicStreak = (activities) => {
    if (!activities || activities.length === 0) return 0;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    let streak = 0;
    let currentDate = new Date(today);
    
    while (true) {
      const hasActivity = activities.some(activity => {
        let activityDate;
        
        if (activity.timestamp) {
          if (activity.timestamp.toDate) {
            activityDate = activity.timestamp.toDate();
          } else {
            activityDate = new Date(activity.timestamp);
          }
        } else if (activity.createdAt) {
          if (activity.createdAt.toDate) {
            activityDate = activity.createdAt.toDate();
          } else {
            activityDate = new Date(activity.createdAt);
          }
        } else if (activity.date) {
          activityDate = new Date(activity.date);
        } else {
          return false;
        }
        
        activityDate.setHours(0, 0, 0, 0);
        return activityDate.getTime() === currentDate.getTime();
      });
      
      if (hasActivity) {
        streak++;
        currentDate.setDate(currentDate.getDate() - 1);
      } else {
        break;
      }
    }
    
    return streak;
  };

  const getBadge = (streak) => {
    if (streak >= 365) return { 
      name: 'Wizard Badge', 
      color: 'from-purple-400 to-indigo-600',
      bgColor: 'from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20',
      borderColor: 'border-purple-200 dark:border-purple-800',
      message: "🧙‍♂️ A year of magical learning! 🎁 You've earned a special gift from the Mahstry team!",
      icon: "sparkles",
      nextMilestone: 365
    };
    if (streak >= 200) return { 
      name: 'Diamond Badge', 
      color: 'from-cyan-400 to-blue-600',
      bgColor: 'from-cyan-50 to-blue-50 dark:from-cyan-900/20 dark:to-blue-900/20',
      borderColor: 'border-cyan-200 dark:border-cyan-800',
      message: "💎 200 days of excellence!",
      icon: "star",
      nextMilestone: 365
    };
    if (streak >= 100) return { 
      name: 'Platinum Badge', 
      color: 'from-gray-400 to-slate-600',
      bgColor: 'from-gray-50 to-slate-50 dark:from-gray-900/20 dark:to-slate-900/20',
      borderColor: 'border-gray-200 dark:border-gray-800',
      message: "💎 100 days of brilliance!",
      icon: "crown",
      nextMilestone: 200
    };
    if (streak >= 60) return { 
      name: 'Gold Badge', 
      color: 'from-yellow-400 to-orange-600',
      bgColor: 'from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20',
      borderColor: 'border-yellow-200 dark:border-yellow-800',
      message: "🥇 Two months of pure dedication!",
      icon: "trophy",
      nextMilestone: 100
    };
    if (streak >= 30) return { 
      name: 'Silver Badge', 
      color: 'from-gray-300 to-gray-500',
      bgColor: 'from-gray-50 to-gray-100 dark:from-gray-900/20 dark:to-gray-800/20',
      borderColor: 'border-gray-200 dark:border-gray-700',
      message: "🥈 A month of excellence!",
      icon: "award",
      nextMilestone: 60
    };
    if (streak >= 7) return { 
      name: 'Bronze Badge', 
      color: 'from-amber-600 to-orange-700',
      bgColor: 'from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20',
      borderColor: 'border-amber-200 dark:border-amber-800',
      message: "🔥 You're building momentum!",
      icon: "flame",
      nextMilestone: 30
    };
    return { 
      name: 'Beginner Badge', 
      color: 'from-green-400 to-blue-500',
      bgColor: 'from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20',
      borderColor: 'border-green-200 dark:border-green-800',
      message: "🚀 Your learning journey has begun!",
      icon: "rocket",
      nextMilestone: 7
    };
  };

  const getBadgeIcon = (iconName) => {
    switch (iconName) {
      case 'sparkles':
        return <Sparkles className="h-12 w-12 text-white" />;
      case 'star':
        return <Star className="h-12 w-12 text-white" />;
      case 'crown':
        return <Crown className="h-12 w-12 text-white" />;
      case 'trophy':
        return <Trophy className="h-12 w-12 text-white" />;
      case 'award':
        return <Award className="h-12 w-12 text-white" />;
      case 'flame':
        return <Flame className="h-12 w-12 text-white" />;
      case 'rocket':
        return <Rocket className="h-12 w-12 text-white" />;
      default:
        return <Flame className="h-12 w-12 text-white" />;
    }
  };

  const getProgressToNextMilestone = (currentStreak, nextMilestone) => {
    const previousMilestone = currentStreak < 7 ? 0 : 
                             currentStreak < 30 ? 7 :
                             currentStreak < 60 ? 30 :
                             currentStreak < 100 ? 60 :
                             currentStreak < 200 ? 100 : 200;
    return Math.min(((currentStreak - previousMilestone) / (nextMilestone - previousMilestone)) * 100, 100);
  };

  const generateBadgeImage = async () => {
    if (!cardRef.current) return null;

    try {
      // Create a canvas with better dimensions for sharing
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Set canvas size for high-quality sharing (1200x800)
      canvas.width = 1200;
      canvas.height = 800;
      
      const currentStreak = streakData?.currentStreak || 0;
      const badge = getBadge(currentStreak);
      const studentName = streakData?.userName || userData?.name || userData?.displayName || 'Student';
      
      // Create gradient background
      const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
      if (badge.name === 'Wizard Badge') {
        gradient.addColorStop(0, '#8b5cf6');
        gradient.addColorStop(1, '#4f46e5');
      } else if (badge.name === 'Diamond Badge') {
        gradient.addColorStop(0, '#06b6d4');
        gradient.addColorStop(1, '#2563eb');
      } else if (badge.name === 'Platinum Badge') {
        gradient.addColorStop(0, '#9ca3af');
        gradient.addColorStop(1, '#475569');
      } else if (badge.name === 'Gold Badge') {
        gradient.addColorStop(0, '#fbbf24');
        gradient.addColorStop(1, '#ea580c');
      } else if (badge.name === 'Silver Badge') {
        gradient.addColorStop(0, '#d1d5db');
        gradient.addColorStop(1, '#6b7280');
      } else if (badge.name === 'Bronze Badge') {
        gradient.addColorStop(0, '#d97706');
        gradient.addColorStop(1, '#c2410c');
      } else {
        gradient.addColorStop(0, '#4ade80');
        gradient.addColorStop(1, '#3b82f6');
      }
      
      // Fill background
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Add subtle pattern overlay
      ctx.globalAlpha = 0.1;
      for (let i = 0; i < canvas.width; i += 50) {
        for (let j = 0; j < canvas.height; j += 50) {
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(i, j, 2, 2);
        }
      }
      ctx.globalAlpha = 1;
      
      // Add glow effect around the badge
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2 - 80;
      const badgeRadius = 120;
      
      // Outer glow
      const glowGradient = ctx.createRadialGradient(centerX, centerY, badgeRadius, centerX, centerY, badgeRadius + 50);
      glowGradient.addColorStop(0, 'rgba(255, 255, 255, 0.3)');
      glowGradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
      ctx.fillStyle = glowGradient;
      ctx.beginPath();
      ctx.arc(centerX, centerY, badgeRadius + 50, 0, 2 * Math.PI);
      ctx.fill();
      
      // Badge circle
      const badgeGradient = ctx.createRadialGradient(centerX - 50, centerY - 50, 0, centerX, centerY, badgeRadius);
      if (badge.name === 'Wizard Badge') {
        badgeGradient.addColorStop(0, '#a855f7');
        badgeGradient.addColorStop(1, '#6366f1');
      } else if (badge.name === 'Diamond Badge') {
        badgeGradient.addColorStop(0, '#22d3ee');
        badgeGradient.addColorStop(1, '#3b82f6');
      } else if (badge.name === 'Platinum Badge') {
        badgeGradient.addColorStop(0, '#cbd5e1');
        badgeGradient.addColorStop(1, '#64748b');
      } else if (badge.name === 'Gold Badge') {
        badgeGradient.addColorStop(0, '#fcd34d');
        badgeGradient.addColorStop(1, '#f97316');
      } else if (badge.name === 'Silver Badge') {
        badgeGradient.addColorStop(0, '#e5e7eb');
        badgeGradient.addColorStop(1, '#9ca3af');
      } else if (badge.name === 'Bronze Badge') {
        badgeGradient.addColorStop(0, '#ea580c');
        badgeGradient.addColorStop(1, '#dc2626');
      } else {
        badgeGradient.addColorStop(0, '#22c55e');
        badgeGradient.addColorStop(1, '#3b82f6');
      }
      
      ctx.fillStyle = badgeGradient;
      ctx.beginPath();
      ctx.arc(centerX, centerY, badgeRadius, 0, 2 * Math.PI);
      ctx.fill();
      
      // Add inner shadow
      ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
      ctx.shadowBlur = 20;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 10;
      
      // Draw flame icon (simplified)
      ctx.fillStyle = '#ffffff';
      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;
      
      // Draw a stylized flame
      ctx.beginPath();
      ctx.moveTo(centerX, centerY - 30);
      ctx.quadraticCurveTo(centerX + 15, centerY - 15, centerX + 12, centerY + 8);
      ctx.quadraticCurveTo(centerX + 20, centerY + 15, centerX + 8, centerY + 30);
      ctx.quadraticCurveTo(centerX, centerY + 22, centerX - 8, centerY + 30);
      ctx.quadraticCurveTo(centerX - 20, centerY + 15, centerX - 12, centerY + 8);
      ctx.quadraticCurveTo(centerX - 15, centerY - 15, centerX, centerY - 30);
      ctx.fill();
      
      // Add text
      ctx.textAlign = 'center';
      ctx.fillStyle = '#ffffff';
      
      // Student name at the top
      ctx.font = 'bold 48px Arial, sans-serif';
      ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
      ctx.shadowBlur = 10;
      ctx.shadowOffsetX = 2;
      ctx.shadowOffsetY = 2;
      ctx.fillText(studentName, centerX, 120);
      
      // Congratulatory message
      ctx.font = 'bold 32px Arial, sans-serif';
      ctx.fillStyle = '#fbbf24';
      ctx.shadowBlur = 5;
      ctx.fillText('🎉 Congratulations! 🎉', centerX, 170);
      
      // Badge name
      ctx.font = 'bold 56px Arial, sans-serif';
      ctx.fillStyle = '#ffffff';
      ctx.shadowBlur = 8;
      ctx.fillText(badge.name, centerX, 240);
      
      // Streak count (large and prominent)
      ctx.font = 'bold 120px Arial, sans-serif';
      ctx.shadowBlur = 15;
      ctx.shadowColor = 'rgba(0, 0, 0, 0.7)';
      ctx.fillText(currentStreak.toString(), centerX, 380);
      
      // Days text
      ctx.font = 'bold 36px Arial, sans-serif';
      ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
      ctx.shadowBlur = 5;
      ctx.fillText('days active', centerX, 430);
      
      // Motivational text
      ctx.font = 'bold 28px Arial, sans-serif';
      ctx.fillStyle = '#fbbf24';
      ctx.shadowBlur = 3;
      ctx.fillText('🔥 Keep the streak alive! 🔥', centerX, 480);
      
      // mahstry.com branding at the bottom
      ctx.font = 'bold 24px Arial, sans-serif';
      ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
      ctx.shadowBlur = 2;
      ctx.fillText('mahstry.com', centerX, 550);
      
      // Add decorative elements
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(centerX, centerY, badgeRadius + 60, 0, 2 * Math.PI);
      ctx.stroke();
      
      ctx.beginPath();
      ctx.arc(centerX, centerY, badgeRadius + 80, 0, 2 * Math.PI);
      ctx.stroke();
      
      // Add sparkles
      ctx.fillStyle = '#ffffff';
      for (let i = 0; i < 8; i++) {
        const angle = (i * Math.PI * 2) / 8;
        const x = centerX + Math.cos(angle) * (badgeRadius + 100);
        const y = centerY + Math.sin(angle) * (badgeRadius + 100);
        ctx.beginPath();
        ctx.arc(x, y, 3, 0, 2 * Math.PI);
        ctx.fill();
      }
      
      // Add mahstry.com branding at the bottom
      ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
      ctx.font = 'bold 24px Arial, sans-serif';
      ctx.fillText('mahstry.com', centerX, canvas.height - 40);
      
      // Add a subtle line above the branding
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(centerX - 80, canvas.height - 60);
      ctx.lineTo(centerX + 80, canvas.height - 60);
      ctx.stroke();
      
      // Convert canvas to blob
      return new Promise((resolve) => {
        canvas.toBlob((blob) => {
          resolve(blob);
        }, 'image/png', 0.95);
      });
    } catch (error) {
      // Error generating badge image
      return null;
    }
  };

  const handleShare = async () => {
    const currentStreak = streakData?.currentStreak || 0;
    const badge = getBadge(currentStreak);
    const shareText = `🔥 I've maintained a ${currentStreak}-day study streak and earned the ${badge.name}! ${badge.message} Keep learning, keep growing! 📚✨`;
    
    try {
      // Generate badge image
      const badgeBlob = await generateBadgeImage();
      
      if (navigator.share && badgeBlob) {
        // Create file from blob
        const badgeFile = new File([badgeBlob], 'badge.png', { type: 'image/png' });
        
        await navigator.share({
          title: 'My Study Streak Badge',
          text: shareText,
          files: [badgeFile]
        });
      } else {
        // Fallback for browsers that don't support Web Share API or file sharing
        if (badgeBlob) {
          // Create download link
          const url = URL.createObjectURL(badgeBlob);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'study-streak-badge.png';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        } else {
          // Fallback to text only
          await navigator.clipboard.writeText(shareText);
        }
      }
    } catch (error) {
      // Error sharing
      // Final fallback to clipboard
      try {
        await navigator.clipboard.writeText(shareText);
      } catch (clipboardError) {
        // Error copying to clipboard
      }
    }
  };

  const handleInfo = () => {
    setShowInfo(true);
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 rounded-xl p-6 border border-green-200 dark:border-green-800">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-300 dark:bg-gray-600 rounded w-1/3 mb-4"></div>
          <div className="h-32 bg-gray-300 dark:bg-gray-600 rounded-full w-32 mx-auto mb-4"></div>
          <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-1/2 mx-auto mb-2"></div>
          <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-1/3 mx-auto"></div>
        </div>
      </div>
    );
  }

  if (error && !streakData) {
    return (
      <div className="bg-gradient-to-br from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 rounded-xl p-6 border border-green-200 dark:border-green-800">
        <div className="text-center text-gray-600 dark:text-gray-400">
          <Flame className="h-8 w-8 mx-auto mb-2 text-orange-500" />
          <p>Unable to load streak data</p>
        </div>
      </div>
    );
  }

  const currentStreak = streakData?.currentStreak || 0;
  const badge = getBadge(currentStreak);

  return (
    <>
      <motion.div
        ref={cardRef}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className={`bg-gradient-to-br ${badge.bgColor} rounded-xl p-6 border ${badge.borderColor} h-full`}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <Flame className="h-6 w-6 text-orange-500" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Study Streak
            </h3>
          </div>
          <div className="flex items-center space-x-2">
            <button 
              onClick={handleInfo}
              className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
            >
              <Info className="h-4 w-4" />
            </button>
            <button 
              onClick={handleShare}
              className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
            >
              <Share2 className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Main Badge */}
        <div className="text-center mb-6">
          <motion.div
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            className={`w-32 h-32 mx-auto mb-4 rounded-full bg-gradient-to-br ${badge.color} flex items-center justify-center shadow-lg relative overflow-hidden`}
          >
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              className="absolute inset-0 opacity-20"
            >
              <div className="absolute top-2 left-2 w-2 h-2 bg-white rounded-full"></div>
              <div className="absolute top-2 right-2 w-1 h-1 bg-white rounded-full"></div>
              <div className="absolute bottom-2 left-2 w-1 h-1 bg-white rounded-full"></div>
              <div className="absolute bottom-2 right-2 w-2 h-2 bg-white rounded-full"></div>
            </motion.div>
            
            <motion.div
              animate={{ y: [-2, 2, -2] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            >
              {getBadgeIcon(badge.icon)}
            </motion.div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-sm font-medium text-green-600 dark:text-green-400 mb-1"
          >
            {badge.name}
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3 }}
            className="text-4xl font-bold text-green-600 dark:text-green-400 mb-1"
          >
            {currentStreak}
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-sm text-gray-600 dark:text-gray-400 mb-3"
          >
            days active
          </motion.div>
          
          {/* Progress to next milestone */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mb-4"
          >
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
              Progress to {badge.nextMilestone} days
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${getProgressToNextMilestone(currentStreak, badge.nextMilestone)}%` }}
                transition={{ delay: 0.6, duration: 1, ease: "easeOut" }}
                className="bg-gradient-to-r from-green-400 to-blue-500 h-2 rounded-full"
              ></motion.div>
            </div>
          </motion.div>
          
          
        </div>

        {/* Daily Activity Status */}
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="mb-4"
        >
          <div className="flex items-center justify-center space-x-2 bg-green-50 dark:bg-green-900/20 rounded-lg p-2 border border-green-200 dark:border-green-800">
            <motion.div
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            >
              <CheckCircle className="h-4 w-4 text-green-500" />
            </motion.div>
            <span className="text-sm font-medium text-green-600 dark:text-green-400">
              Active today
            </span>
          </div>
        </motion.div>

        {/* Motivational Message */}
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="mb-6"
        >
          <div className="flex items-center justify-center space-x-2 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-2 border border-yellow-200 dark:border-yellow-800">
            <motion.div
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            >
              <Sun className="h-4 w-4 text-yellow-500" />
            </motion.div>
            <span className="text-sm font-medium text-yellow-600 dark:text-yellow-400">
              Keep going!
            </span>
          </div>
        </motion.div>

        {/* Statistics */}
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0 }}
          className="grid grid-cols-2 gap-4"
        >
          <motion.div 
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 1.1 }}
            className="text-center bg-white/50 dark:bg-gray-800/50 rounded-lg p-3 border border-gray-200 dark:border-gray-700"
          >
            <div className="text-lg font-bold text-gray-900 dark:text-white">
              {streakData?.longestStreak || currentStreak}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              Longest streak
            </div>
          </motion.div>
          <motion.div 
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 1.2 }}
            className="text-center bg-white/50 dark:bg-gray-800/50 rounded-lg p-3 border border-gray-200 dark:border-gray-700"
          >
            <div className="text-lg font-bold text-gray-900 dark:text-white">
              {streakData?.totalActiveDays || currentStreak}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              Total active days
            </div>
          </motion.div>
        </motion.div>
      </motion.div>

      {/* Info Modal */}
      {showInfo && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowInfo(false)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Study Streak Info
              </h3>
              <button
                onClick={() => setShowInfo(false)}
                className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="space-y-4 text-sm text-gray-600 dark:text-gray-400">
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white mb-2">How it works:</h4>
                <ul className="space-y-1 list-disc list-inside">
                  <li>Your streak increases each day you study</li>
                  <li>Missing a day resets your streak to 1</li>
                  <li>Earn badges at 7, 30, 60, 100, 200, and 365 days</li>
                  <li>Track your progress towards the next milestone</li>
                </ul>
              </div>
              
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white mb-2">Badge Levels:</h4>
                <div className="space-y-2">
                  <div className="flex items-center space-x-3">
                    <div className="w-4 h-4 rounded-full bg-gradient-to-br from-green-400 to-blue-500"></div>
                    <span>Beginner Badge (0-6 days)</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-4 h-4 rounded-full bg-gradient-to-br from-amber-600 to-orange-700"></div>
                    <span>Bronze Badge (7+ days)</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-4 h-4 rounded-full bg-gradient-to-br from-gray-300 to-gray-500"></div>
                    <span>Silver Badge (30+ days)</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-4 h-4 rounded-full bg-gradient-to-br from-yellow-400 to-orange-600"></div>
                    <span>Gold Badge (60+ days)</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-4 h-4 rounded-full bg-gradient-to-br from-gray-400 to-slate-600"></div>
                    <span>Platinum Badge (100+ days)</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-4 h-4 rounded-full bg-gradient-to-br from-cyan-400 to-blue-600"></div>
                    <span>Diamond Badge (200+ days)</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-4 h-4 rounded-full bg-gradient-to-br from-purple-400 to-indigo-600"></div>
                    <span>Wizard Badge (365+ days)</span>
                  </div>
                </div>
                
                {/* Wizard Badge Gift Information */}
                <div className="mt-6 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                  <div className="flex items-center space-x-2 mb-2">
                    <Crown className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                    <span className="font-semibold text-purple-800 dark:text-purple-200">Wizard Badge Reward</span>
                  </div>
                  <p className="text-sm text-purple-700 dark:text-purple-300">
                    🎁 Reach the Wizard Badge (365+ days) and receive a special gift from the Mahstry team!
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </>
  );
};

export default StudyStreakCard;
